<template>
  <div
    class="db-funnel-results-list db-results-wrapper-package"
    :class="`db-results-wrapper-${
      nbrGridLayout === 2
        ? isMobile
          ? 3
          : 2
        : nbrGridLayout === 1 || nbrGridLayout === 3
        ? nbrGridLayout
        : 'none'
    }`"
  >
    <div
      v-for="packageItem in packages"
      :key="packageItem.id"
    >
      <DbCard class="db-tw-h-full">
        <template v-slot:illustration>
          <DbCardIllustration
            v-if="packageItem.photos"
            :title="packageItem.name"
            :photos="packageItem.photos"
            :showFullscreenButton="false"
          />
        </template>
        <template v-slot:content>
          <DbCardContentPackage
            v-on:click-details="$emit('click-details', packageItem)"
            v-on:click-select="$emit('click-select', packageItem)"
          >
            <template v-slot:title>
              {{ packageItem.name }}
            </template>

            <template v-slot:rooms>
              <DbListFunctionSetupRoom
                v-if="packageItem.functionRoomSetups && packageItem.functionRoomSetups.length !== 0"
                :listFunctRoomSetup="packageItem.functionRoomSetups"
                :dynamicWidthWithCallBack="() => $emit('click-details', packageItem)"
              />
            </template>

            <template
              v-if="
                packageItem.short_description &&
                getDescription(packageItem.short_description) &&
                getDescription(packageItem.short_description)?.length !== 0
              "
              v-slot:description
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div
                class="db-wysiwyg"
                :class="{ 'db-no-margin': packageItem.functionRoomSetups.length <= 1 }"
                v-html="getDescription(packageItem.short_description)"
              />
            </template>

            <template
              v-if="
                seeSellingPoints !== 2 &&
                packageItem.package_inclusion &&
                packageItem.package_inclusion.length !== 0
              "
              v-slot:inclusions
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div
                :class="{ 'db-package-inclusion-limit-overflow': seeSellingPoints === 3 }"
                v-html="packageItem.package_inclusion"
              />
            </template>

            <template
              v-if="packageItem.displayPrice"
              v-slot:rate
            >
              <DbRate class="db-card-rate db-text-right">
                <template v-slot:prefix>
                  {{ configStore.funnel.showVenueSelection ? $t('From') : '' }}
                </template>
                <template v-slot:rate>
                  <PriceDisplay
                    v-if="packageItem.displayPrice"
                    :value="
                      seeTotalPrice
                        ? packageItem.displayPrice.valueTotal()
                        : packageItem.displayPrice.value
                    "
                    :currencyCode="packageItem.displayPrice.currencyCode"
                  />
                </template>
                <template
                  v-if="nbrGridLayout !== 1"
                  v-slot:suffix
                >
                  {{ seeTotalPrice ? $t('Total per day') : $t('Per person/day') }}
                </template>
                <template
                  v-else
                  v-slot:details
                >
                  {{ seeTotalPrice ? $t('Total per day') : $t('Per person/day') }}
                </template>
                <template
                  v-slot:footernote
                  v-if="packageItem.taxes == 'inclusive'"
                >
                  {{ $t('Inclusive of taxes and fees') }}
                </template>
                <template
                  v-slot:footernote
                  v-else-if="packageItem.taxes == 'exclusive'"
                >
                  {{ $t('Exclusive of taxes and fees') }}
                </template>
                <template
                  v-slot:footernote
                  v-else-if="packageItem.taxes == 'subject'"
                >
                  {{ $t('Subject to additional taxes and fees') }}
                </template>
              </DbRate>
            </template>
          </DbCardContentPackage>
        </template>
      </DbCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHtmlTruncate } from '@/composables/useHtmlTruncate'
import type { SearchPackage } from '@/pages/booking-funnel/types/SearchPackage'
import type { DescriptionFormat } from '@/types/galaxy-data/unit'
import { useConfigurations } from '@/stores/configurations'
import DbListFunctionSetupRoom from '../DbListFunctionSetupRoom.vue'
import { useBreakpoints } from '@vueuse/core'

const configStore = useConfigurations()

const props = withDefaults(
  defineProps<{
    packages: SearchPackage[]
    seeTotalPrice: boolean
    descriptionDisplay?: DescriptionFormat
    seeSellingPoints?: number
    nbrGridLayout?: number
  }>(),
  {
    nbrGridLayout: 1,
  },
)

defineEmits<{
  (e: 'click-details', packageItem: SearchPackage): void
  (e: 'click-select', packageItem: SearchPackage): void
}>()

// TODO: duplicated
function getDescription(desc: string) {
  if (props.descriptionDisplay) {
    switch (props.descriptionDisplay) {
      case 'none':
        return null
      case 'truncate':
        return useHtmlTruncate(desc)
      case 'full':
        return desc
    }
  }
  return desc
}

const breakpoints = useBreakpoints({ mobile: 992 })
const isMobile = breakpoints.smaller('mobile')
</script>

<style scoped>
.db-app .db-package-inclusion-limit-overflow {
  text-overflow: ellipsis;
  white-space: normal;
  overflow: hidden;
  max-height: 115px;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
}

.db-app .db-package-inclusion-limit-overflow:empty {
  display: none !important;
}

/** 3 on 1 line */
.db-app .db-funnel-results-list.db-results-wrapper-3 {
  grid-template-columns: repeat(3, minmax(300px, 1fr));
}

/** 2 on 1 line */
.db-app .db-funnel-results-list.db-results-wrapper-2 {
  grid-template-columns: repeat(2, minmax(300px, 1fr));
}

/** 1 on 1 line */
.db-app .db-funnel-results-list.db-results-wrapper-1,
.db-app .db-funnel-results-list.db-results-wrapper- {
  grid-template-columns: 1fr;
}

/** On mobile, put 1 on line */
@media screen and (max-width: 680px) {
  .db-app .db-funnel-results-list.db-results-wrapper-3 {
    grid-template-columns: minmax(300px, 1fr);
  }
  .db-app .db-funnel-results-list.db-results-wrapper-2 {
    grid-template-columns: minmax(300px, 1fr);
  }
}

/** On tablet, force put 1 -> 2 on 1 line */
@media (min-width: 680px) and (max-width: 992px) {
  .db-app .db-funnel-results-list.db-results-wrapper-3 {
    grid-template-columns: repeat(2, minmax(300px, 1fr));
  }
}
</style>
